export const LEGAL_QUIZ_VERSION = 6;
export const LEGAL_QUIZ_MIXED_TOTAL = 30;

export const legalQuizDecks = {
  constitutional: 'constitutional',
  scenarios: 'scenarios',
  witnessing: 'witnessing',
  signals: 'signals',
  unsafeResponses: 'unsafe-responses',
  phraseRecall: 'phrase-recall',
  iceEncounters: 'ice-encounters',
  trafficStops: 'traffic-stops',
  protestRights: 'protest-rights',
  firstAid: 'first-aid',
  deEscalation: 'de-escalation',
};

export const legalQuizQuestions = [
  {
    id: 'constitution-1st-recording',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '1st-amendment',
    difficulty: 'easy',
    prompt: 'Which amendment most directly protects your right to record ICE or police in public where you are legally present?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '4th Amendment' },
      { id: 'c', text: '5th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'a',
    explanation:
      'Recording police and ICE in public spaces is tied most directly to 1st Amendment protections for speech, press, and documenting public officials.',
    reinforcement:
      'If you are in a public place and not interfering, you can document what officers do.',
    sourceRefs: ['legal.amendment1st', 'legal.recordingYouCanRecord'],
    tags: ['rights', 'recording', '1st-amendment'],
  },
  {
    id: 'constitution-1st-assembly',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '1st-amendment',
    difficulty: 'easy',
    prompt: 'Peacefully gathering in a public space to protest is most directly protected by which amendment?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '5th Amendment' },
      { id: 'c', text: '6th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'a',
    explanation:
      'The 1st Amendment protects peaceful assembly, protest, and petitioning the government.',
    reinforcement: 'Peaceful assembly and political expression are core 1st Amendment protections.',
    sourceRefs: ['legal.rightToProtestAssemble', 'legal.protestPeacefulAssembly'],
    tags: ['rights', 'assembly', '1st-amendment'],
  },
  {
    id: 'constitution-1st-not-protected',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '1st-amendment',
    difficulty: 'medium',
    prompt: 'Which kind of speech is not protected by the 1st Amendment?',
    choices: [
      { id: 'a', text: 'Criticizing the government or immigration policy' },
      { id: 'b', text: 'Carrying signs in a peaceful protest' },
      { id: 'c', text: 'True threats or incitement to imminent lawless action' },
      { id: 'd', text: 'Documenting a public encounter on your phone' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Political criticism is strongly protected, but true threats and incitement to imminent lawless action are not.',
    reinforcement:
      'Protected speech is broad, but threats and incitement are outside that protection.',
    sourceRefs: ['legal.speechProtected', 'legal.speechNotProtected'],
    tags: ['rights', 'speech', '1st-amendment'],
  },
  {
    id: 'constitution-4th-searches',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '4th-amendment',
    difficulty: 'easy',
    prompt: 'Which amendment protects against unreasonable searches and seizures?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '4th Amendment' },
      { id: 'c', text: '5th Amendment' },
      { id: 'd', text: '6th Amendment' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The 4th Amendment is the main constitutional protection against unreasonable searches and seizures.',
    reinforcement: '4th Amendment = search and seizure protection.',
    sourceRefs: ['legal.amendment4th', 'legal.whatThisMeansForYou'],
    tags: ['rights', 'searches', '4th-amendment'],
  },
  {
    id: 'constitution-4th-home-entry',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '4th-amendment',
    difficulty: 'medium',
    prompt: 'What kind of warrant does ICE need to enter your home?',
    choices: [
      { id: 'a', text: 'An administrative warrant signed by ICE' },
      { id: 'b', text: 'Any document with an agency seal' },
      { id: 'c', text: 'A judicial warrant signed by a judge' },
      { id: 'd', text: 'No warrant if they ask loudly enough' },
    ],
    correctChoiceId: 'c',
    explanation:
      'A judge-signed judicial warrant is the key requirement for entry into a home. An administrative warrant is not enough.',
    reinforcement:
      'Administrative warrants do not authorize entry into your home.',
    sourceRefs: ['legal.fourthYourHome', 'legal.fourthAdminVsJudicial', 'scenarioData.doorStep3Script'],
    tags: ['rights', 'home', 'warrants', '4th-amendment'],
  },
  {
    id: 'constitution-4th-admin-warrant',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '4th-amendment',
    difficulty: 'medium',
    prompt: 'What is true about an administrative warrant signed by ICE?',
    choices: [
      { id: 'a', text: 'It gives ICE authority to enter your home.' },
      { id: 'b', text: 'It does not authorize entry into your home.' },
      { id: 'c', text: 'It automatically forces you to answer questions.' },
      { id: 'd', text: 'It replaces your right to remain silent.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Administrative warrants such as I-200 or I-205 are not the same as judge-signed judicial warrants and do not authorize home entry.',
    reinforcement:
      'ICE-signed administrative warrant is not enough to enter your home.',
    sourceRefs: ['legal.fourthAdminVsJudicial', 'scenarioData.doorWarrantAdminDesc'],
    tags: ['rights', 'warrants', '4th-amendment'],
  },
  {
    id: 'constitution-5th-silence',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '5th-amendment',
    difficulty: 'easy',
    prompt: 'Which amendment most directly supports your right to remain silent when questioned?',
    choices: [
      { id: 'a', text: '4th Amendment' },
      { id: 'b', text: '5th Amendment' },
      { id: 'c', text: '6th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The 5th Amendment protects against self-incrimination, which is why you can refuse to answer questions.',
    reinforcement: '5th Amendment = right to remain silent.',
    sourceRefs: ['legal.amendment5th', 'legal.fifthCanStaySilent'],
    tags: ['rights', 'silence', '5th-amendment'],
  },
  {
    id: 'constitution-6th-lawyer',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '6th-amendment',
    difficulty: 'easy',
    prompt: 'Which amendment is most directly tied to asking for a lawyer in criminal proceedings?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '5th Amendment' },
      { id: 'c', text: '6th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'c',
    explanation:
      'The 6th Amendment protects the right to counsel in criminal proceedings, which is why asking for a lawyer matters before answering more questions.',
    reinforcement: '6th Amendment = right to counsel.',
    sourceRefs: ['legal.amendment6th', 'legal.sixthRightToLawyer'],
    tags: ['rights', 'lawyer', '6th-amendment'],
  },
  {
    id: 'constitution-6th-immigration-court',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '6th-amendment',
    difficulty: 'medium',
    prompt: 'What is true about a free lawyer in immigration court?',
    choices: [
      { id: 'a', text: 'A free lawyer is always guaranteed in immigration court.' },
      { id: 'b', text: 'There is no guaranteed right to a free attorney in immigration court.' },
      { id: 'c', text: 'You cannot have a lawyer in immigration court.' },
      { id: 'd', text: 'Only citizens can ask for legal help there.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Immigration court is different from a criminal prosecution. There is no guaranteed right to a free attorney in immigration court.',
    reinforcement:
      'Immigration court does not guarantee a free lawyer, so pro bono help matters.',
    sourceRefs: ['legal.sixthImmigrationCourt'],
    tags: ['rights', 'lawyer', 'immigration-court', '6th-amendment'],
  },
  {
    id: 'constitution-14th-everyone',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '14th-amendment',
    difficulty: 'medium',
    prompt: 'Which amendment emphasizes that due process and equal protection apply to all persons on U.S. soil, not just citizens?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '4th Amendment' },
      { id: 'c', text: '6th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'd',
    explanation:
      'The 14th Amendment uses the phrase “any person,” which is why due process and equal protection do not belong only to citizens.',
    reinforcement: '14th Amendment = due process and equal protection for all persons.',
    sourceRefs: ['legal.amendment14th', 'legal.fourteenthAppliesToEveryone'],
    tags: ['rights', 'due-process', 'equal-protection', '14th-amendment'],
  },
  {
    id: 'constitution-admin-vs-judicial',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: 'warrants',
    difficulty: 'medium',
    prompt: 'Which statement is correct?',
    choices: [
      { id: 'a', text: 'An administrative warrant gives ICE full authority to enter your home.' },
      { id: 'b', text: 'A judicial warrant signed by a judge can authorize home entry.' },
      { id: 'c', text: 'Any officer request automatically becomes consent.' },
      { id: 'd', text: 'You should open the door first and ask questions later.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Only a judicial warrant signed by a judge can authorize entry into a home. An administrative warrant does not do that.',
    reinforcement:
      'Judge-signed judicial warrant matters; administrative warrant is not enough for home entry.',
    sourceRefs: ['legal.fourthAdminVsJudicial', 'scenarioData.doorBranch3bExplanation'],
    tags: ['rights', 'warrants', 'door'],
  },
  {
    id: 'constitution-consent-phrase',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '4th-amendment',
    difficulty: 'easy',
    prompt: 'Which phrase best refuses a search?',
    choices: [
      { id: 'a', text: 'You can look around if you are quick.' },
      { id: 'b', text: 'I do not consent to any searches.' },
      { id: 'c', text: 'Maybe later, after I explain things.' },
      { id: 'd', text: 'Only if I have nothing to hide.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Non-consent should be clear, calm, and on the record. This phrasing does that directly.',
    reinforcement: 'Say it clearly: “I do not consent to any searches.”',
    sourceRefs: ['legal.fourthConsent', 'scenarioData.streetStep3Script', 'scenarioData.vehicleStep4Script'],
    tags: ['phrase', 'searches', '4th-amendment'],
  },
  {
    id: 'constitution-lawyer-phrase',
    type: 'multipleChoice',
    deck: legalQuizDecks.constitutional,
    category: '6th-amendment',
    difficulty: 'easy',
    prompt: 'Which phrase most clearly invokes counsel?',
    choices: [
      { id: 'a', text: 'Can I maybe call someone later?' },
      { id: 'b', text: 'I know my rights.' },
      { id: 'c', text: 'I want to speak to a lawyer.' },
      { id: 'd', text: 'I will explain after this is over.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'A clear request leaves less room for confusion than a vague or delayed one. When pressure rises, plain language protects better than hints.',
    reinforcement: 'Direct is better: “I want to speak to a lawyer.”',
    sourceRefs: ['legal.fifthRequestLawyer', 'legal.sixthHowToAssert', 'scenarioData.streetStep4Script'],
    tags: ['phrase', 'lawyer', '6th-amendment'],
  },

  {
    id: 'scenario-door-first',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'door',
    difficulty: 'easy',
    prompt: 'If ICE is at your door, what is the first move?',
    choices: [
      { id: 'a', text: 'Open the door and talk calmly face-to-face.' },
      { id: 'b', text: 'Step outside and ask what they want.' },
      { id: 'c', text: 'Do not open the door.' },
      { id: 'd', text: 'Hand them your documents through the door.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'The home is one of the strongest protected spaces. Keeping the door closed preserves that boundary; opening it too early gives part of that protection away.',
    reinforcement: 'At home, start with the closed door as your boundary.',
    sourceRefs: ['scenarioData.doorStep1Action', 'scenarioData.doorStep1Explanation'],
    tags: ['scenario', 'door', 'first-step'],
  },
  {
    id: 'scenario-door-warrant-request',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'door',
    difficulty: 'medium',
    prompt: 'At the door, what should you ask them to do with the warrant?',
    choices: [
      { id: 'a', text: 'Read it out loud from outside.' },
      { id: 'b', text: 'Leave it on the porch and come back later.' },
      { id: 'c', text: 'Slide it under the door or show it through a window.' },
      { id: 'd', text: 'Text a picture of it to your phone.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Ask to inspect the warrant without opening the door by having it slid under the door or shown through a window.',
    reinforcement: 'Inspect the warrant without giving up the protection of a closed door.',
    sourceRefs: ['scenarioData.doorStep3Script', 'scenarioData.doorKeyPoint2'],
    tags: ['scenario', 'door', 'warrant'],
  },
  {
    id: 'scenario-door-admin-warrant',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'door',
    difficulty: 'medium',
    prompt: 'If they show an administrative warrant like an I-200 or I-205 at your door, what should you do?',
    choices: [
      { id: 'a', text: 'Open the door because they showed official paperwork.' },
      { id: 'b', text: 'Say it is not a judicial warrant and you are not required to open the door.' },
      { id: 'c', text: 'Hand over your documents through the door right away.' },
      { id: 'd', text: 'Step outside to read it more closely.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'An administrative warrant does not authorize entry into a home. The safer response is to keep the door closed and say so plainly.',
    reinforcement:
      'Administrative warrant is not the same as a judge-signed judicial warrant.',
    sourceRefs: ['scenarioData.doorBranch3bScript', 'scenarioData.doorBranch3bExplanation'],
    tags: ['scenario', 'door', 'warrant'],
  },
  {
    id: 'scenario-door-entry-without-consent',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'door',
    difficulty: 'hard',
    prompt: 'If they enter your home without consent or a valid judicial warrant, which response is best?',
    choices: [
      { id: 'a', text: 'Physically block them from moving any farther.' },
      { id: 'b', text: 'I do not consent to your presence in my home. I am not resisting, but I do not consent. I want to speak to a lawyer immediately.' },
      { id: 'c', text: 'Explain your full immigration history so they leave faster.' },
      { id: 'd', text: 'Sign whatever they hand you so you can challenge it later.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'If entry happens anyway, the safest response is verbal non-consent, no physical resistance, and an immediate lawyer request.',
    reinforcement:
      'If entry happens anyway: do not physically resist, but say non-consent clearly for the record.',
    sourceRefs: ['scenarioData.doorBranch4aScript', 'scenarioData.doorBranch4aExplanation'],
    tags: ['scenario', 'door', 'entry'],
  },
  {
    id: 'scenario-street-first-question',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'street',
    difficulty: 'easy',
    prompt: 'If you are stopped on the street, what is the first question to ask?',
    choices: [
      { id: 'a', text: 'Why are you doing this to me?' },
      { id: 'b', text: 'Am I free to leave?' },
      { id: 'c', text: 'Do you know my status?' },
      { id: 'd', text: 'Can I call my family first?' },
    ],
    correctChoiceId: 'b',
    explanation:
      'That question separates a voluntary encounter from a detention. Before anything else, you need to know whether you are free to walk away.',
    reinforcement: 'Street stop first question: “Am I free to leave?”',
    sourceRefs: ['scenarioData.streetStep1Script', 'scenarioData.streetKeyPoint1'],
    tags: ['scenario', 'street', 'phrase'],
  },
  {
    id: 'scenario-street-after-detention',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'street',
    difficulty: 'medium',
    prompt: 'If they say you are not free to leave on the street, which response is best?',
    choices: [
      { id: 'a', text: 'Run away before they can do anything else.' },
      { id: 'b', text: 'Argue about every detail on the spot.' },
      { id: 'c', text: 'Ask the legal basis for detention, remain silent, and ask for a lawyer.' },
      { id: 'd', text: 'Answer a few questions to clear things up.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'If you are being detained, the next steady move is simple: ask the legal basis, stop talking, and ask for a lawyer.',
    reinforcement:
      'If detained: ask the legal basis, stay silent, ask for counsel.',
    sourceRefs: ['scenarioData.streetBranch1bScript', 'scenarioData.streetStep2Script', 'scenarioData.streetStep4Script'],
    tags: ['scenario', 'street', 'detention'],
  },
  {
    id: 'scenario-street-search-anyway',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'street',
    difficulty: 'medium',
    prompt: 'If they search you anyway after you refused on the street, what should you do?',
    choices: [
      { id: 'a', text: 'Physically pull away so the refusal counts.' },
      { id: 'b', text: 'Stay silent and never mention it again.' },
      { id: 'c', text: 'Say: “I do not consent to this search. I am not resisting, but I am not consenting.”' },
      { id: 'd', text: 'Start arguing every detail of the law on the scene.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'A calm verbal refusal keeps the record clear without adding physical resistance. The point is to preserve the issue without escalating the moment.',
    reinforcement:
      'Repeat non-consent clearly; do not physically resist.',
    sourceRefs: ['scenarioData.streetBranch3aScript', 'scenarioData.streetBranch3aExplanation'],
    tags: ['scenario', 'street', 'searches'],
  },
  {
    id: 'scenario-vehicle-documents',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'vehicle',
    difficulty: 'easy',
    prompt: 'During a vehicle stop, what should you provide when asked as the driver?',
    choices: [
      { id: 'a', text: 'Your full immigration history' },
      { id: 'b', text: 'Only whatever the officer guesses you have' },
      { id: 'c', text: 'Driver license and registration, while announcing before reaching' },
      { id: 'd', text: 'Your phone and unlocked apps' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Provide the legally required documents and announce before reaching so the movement is clear and does not escalate the stop.',
    reinforcement:
      'Vehicle stop basics: keep hands visible, announce movement, provide license and registration.',
    sourceRefs: ['scenarioData.vehicleStep3Script', 'scenarioData.vehicleBranch2aScript'],
    tags: ['scenario', 'vehicle', 'documents'],
  },
  {
    id: 'scenario-vehicle-consent',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'vehicle',
    difficulty: 'medium',
    prompt: 'If they ask to search your vehicle, what should you do?',
    choices: [
      { id: 'a', text: 'Consent if you think the stop will end sooner' },
      { id: 'b', text: 'Say: “I do not consent to any searches of my vehicle.”' },
      { id: 'c', text: 'Stay silent but nod yes' },
      { id: 'd', text: 'Unlock everything to avoid suspicion' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Consent matters. Even if officers search anyway, a clear refusal keeps your position intact instead of surrendering it in your own words.',
    reinforcement: 'Vehicle search request: refuse consent clearly and calmly.',
    sourceRefs: ['scenarioData.vehicleBranch3aScript', 'scenarioData.vehicleWarrantAdminDesc'],
    tags: ['scenario', 'vehicle', 'searches'],
  },
  {
    id: 'scenario-vehicle-passenger',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'vehicle',
    difficulty: 'medium',
    prompt: 'If you are a passenger during a vehicle stop, which response is closest to the guidance?',
    choices: [
      { id: 'a', text: 'I must provide identification in every state no matter what.' },
      { id: 'b', text: 'Am I free to leave? I am a passenger and in most states I am not required to provide identification.' },
      { id: 'c', text: 'I should unlock the car and phone for everyone.' },
      { id: 'd', text: 'The driver has rights, but passengers do not.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Passengers often have stronger rights than drivers and can ask whether they are free to leave.',
    reinforcement:
      'Passengers can often ask to leave and may not be required to provide ID in most states.',
    sourceRefs: ['scenarioData.vehicleBranch2bScript', 'scenarioData.vehicleBranch2bExplanation'],
    tags: ['scenario', 'vehicle', 'passenger'],
  },
  {
    id: 'scenario-workplace-silence',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'workplace',
    difficulty: 'medium',
    prompt: 'At work, which response is safest if officers begin questioning you?',
    choices: [
      { id: 'a', text: 'Run out a side door before they can talk to you.' },
      { id: 'b', text: 'I am exercising my right to remain silent. I want to speak to a lawyer.' },
      { id: 'c', text: 'I will show any documents you ask for right now.' },
      { id: 'd', text: 'My employer can answer for me, so I will too.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'At work, sudden movement creates risk. Calm silence and a direct request for counsel are steadier than panic, explanation, or flight.',
    reinforcement:
      'At work: do not run, stay calm, invoke silence, ask for a lawyer.',
    sourceRefs: ['scenarioData.workplaceStep1Script', 'scenarioData.workplaceStep2Script'],
    tags: ['scenario', 'workplace', 'silence'],
  },
  {
    id: 'scenario-workplace-private-areas',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'workplace',
    difficulty: 'medium',
    prompt: 'What is true about ICE entering non-public workplace areas?',
    choices: [
      { id: 'a', text: 'They can always enter private work areas without a warrant.' },
      { id: 'b', text: 'They need a judicial warrant to enter non-public workplace areas.' },
      { id: 'c', text: 'Any manager permission automatically replaces a warrant.' },
      { id: 'd', text: 'Only a posted sign matters.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Non-public workplace areas are treated more like home or private space, so a judicial warrant is required for entry.',
    reinforcement:
      'Non-public workplace areas keep stronger protections than public lobbies or customer areas.',
    sourceRefs: ['scenarioData.workplaceStep5Script', 'scenarioData.workplaceWarrantJudicialDesc'],
    tags: ['scenario', 'workplace', 'warrant'],
  },
  {
    id: 'scenario-border-location',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'border',
    difficulty: 'medium',
    prompt: 'What should you identify first during a border encounter?',
    choices: [
      { id: 'a', text: 'Whether the officers look calm or angry' },
      { id: 'b', text: 'Whether this is a port of entry or an internal checkpoint' },
      { id: 'c', text: 'Whether your phone battery is full' },
      { id: 'd', text: 'Whether witnesses are recording' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The first question is where you are. A port of entry and an internal checkpoint are not the same place, and they do not work under the same rules.',
    reinforcement:
      'At the border, first identify what kind of checkpoint you are actually at.',
    sourceRefs: ['scenarioData.borderStep1Script', 'scenarioData.borderOverview'],
    tags: ['scenario', 'border', 'checkpoint'],
  },
  {
    id: 'scenario-border-device-search',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'border',
    difficulty: 'hard',
    prompt: 'If officers want to search your phone or device during a border-related stop, which answer is best?',
    choices: [
      { id: 'a', text: 'Unlock it immediately because devices are always outside your rights.' },
      { id: 'b', text: 'I do not consent to a search of my electronic device. I want to speak to a lawyer before any device access.' },
      { id: 'c', text: 'Delete things quickly in front of them.' },
      { id: 'd', text: 'Only refuse if you are a U.S. citizen.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A clear non-consent phrase for electronic devices preserves the issue for the record and can be paired with a lawyer request.',
    reinforcement:
      'Even where agents claim broad authority, state non-consent clearly for the record.',
    sourceRefs: ['scenarioData.borderBranch5bScript', 'scenarioData.borderBranch5bExplanation'],
    tags: ['scenario', 'border', 'devices'],
  },
  {
    id: 'scenario-border-port-entry',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'border',
    difficulty: 'medium',
    prompt: 'At an actual port of entry, which response is best?',
    choices: [
      { id: 'a', text: 'Refuse all citizenship questions immediately.' },
      { id: 'b', text: 'State your citizenship status clearly and briefly, and do not volunteer extra information.' },
      { id: 'c', text: 'Lie if you think it will get you through faster.' },
      { id: 'd', text: 'Ports of entry work exactly like internal checkpoints.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Ports of entry are more restrictive than internal checkpoints. At the actual border, answer basic citizenship questions truthfully and briefly without volunteering extra information.',
    reinforcement:
      'Port of entry is more restrictive than an internal checkpoint.',
    sourceRefs: ['scenarioData.borderStep2Script', 'scenarioData.borderStep2Explanation'],
    tags: ['scenario', 'border', 'port-of-entry'],
  },
  {
    id: 'scenario-street-steady-first',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'street',
    difficulty: 'easy',
    prompt: 'If you have one brief moment before answering on the street, what steadies the encounter most?',
    choices: [
      { id: 'a', text: 'Take one slower breath, keep your hands visible, and use a short question.' },
      { id: 'b', text: 'Explain your whole story quickly before they interrupt.' },
      { id: 'c', text: 'Start arguing the law in detail right away.' },
      { id: 'd', text: 'Reach for your phone and your wallet at the same time.' },
    ],
    correctChoiceId: 'a',
    explanation:
      'A slower breath and visible hands lower confusion for everyone. Short questions are easier to hold onto than long explanations once pressure has already narrowed attention.',
    reinforcement:
      'Steadiness usually begins with breath, visibility, and one clear sentence.',
    sourceRefs: ['scenarioData.streetStep1Script', 'scenarioData.streetKeyPoint2'],
    tags: ['scenario', 'street', 'calm', 'breathing'],
  },
  {
    id: 'scenario-vehicle-announce-movement',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'vehicle',
    difficulty: 'medium',
    prompt: 'Before reaching for documents during a vehicle stop, what is the safest move?',
    choices: [
      { id: 'a', text: 'Reach quickly so officers know you are cooperating.' },
      { id: 'b', text: 'Tell the officer what you are about to reach for, then move slowly.' },
      { id: 'c', text: 'Turn your body away and search through every pocket at once.' },
      { id: 'd', text: 'Wait in silence and hope they stop asking.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Announcing movement makes the next action easier to read. Slow, visible motion is often safer than silent motion that leaves others guessing.',
    reinforcement:
      'Say what you are reaching for before your hands move.',
    sourceRefs: ['scenarioData.vehicleStep3Script', 'scenarioData.vehicleKeyPoint3'],
    tags: ['scenario', 'vehicle', 'documents', 'calm'],
  },
  {
    id: 'scenario-workplace-short-answers',
    type: 'multipleChoice',
    deck: legalQuizDecks.scenarios,
    category: 'workplace',
    difficulty: 'medium',
    prompt: 'If fear makes your mind race at work, what kind of response is safest?',
    choices: [
      { id: 'a', text: 'Long answers that try to prove you have done nothing wrong.' },
      { id: 'b', text: 'Short rights-based answers, then silence until counsel is present.' },
      { id: 'c', text: 'Talking faster so you can get through the moment.' },
      { id: 'd', text: 'Signing forms first and reading them later.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'When the mind races, shorter language is easier to keep true. Rights-based answers protect better than explanations built in fear.',
    reinforcement:
      'Under pressure, fewer words often preserve more ground.',
    sourceRefs: ['scenarioData.workplaceStep2Script', 'scenarioData.workplaceKeyPoint2'],
    tags: ['scenario', 'workplace', 'calm', 'phrase'],
  },
  {
    id: 'witness-role-first-duty',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'easy',
    prompt: 'As a community witness, what is your first duty?',
    choices: [
      { id: 'a', text: 'Take over the encounter and answer for the person being stopped.' },
      { id: 'b', text: 'Stay calm, observe, and document without escalating the scene.' },
      { id: 'c', text: 'Move as close as possible to pressure officers with your presence.' },
      { id: 'd', text: 'Argue immigration law on the sidewalk.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A witness helps most by staying steady enough to observe clearly. Documentation is strongest when it does not become another source of chaos.',
    reinforcement:
      'Witness first. Do not become the center of the encounter.',
    sourceRefs: ['communityWitnessing.roleBody', 'communityWitnessing.documentTitle'],
    tags: ['witness', 'documentation', 'practice'],
  },
  {
    id: 'witness-positioning-distance',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'easy',
    prompt: 'Where should a witness usually stand?',
    choices: [
      { id: 'a', text: 'Close enough to touch the officers if needed.' },
      { id: 'b', text: 'At a calm, visible distance where you can record without interfering.' },
      { id: 'c', text: 'Hidden behind a building so no one sees you.' },
      { id: 'd', text: 'Directly between officers and the person being questioned.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Distance protects both the witness and the person being documented. A visible, non-interfering position keeps the record clearer and the risk lower.',
    reinforcement:
      'Witness from distance. Visibility matters; interference does not help.',
    sourceRefs: ['communityWitnessing.positioningBody', 'communityWitnessing.posRule1'],
    tags: ['witness', 'positioning'],
  },
  {
    id: 'witness-document-priority',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'medium',
    prompt: 'Which detail is most useful to capture early if you are recording?',
    choices: [
      { id: 'a', text: 'The witness talking about their own opinions.' },
      { id: 'b', text: 'The weather and nearby storefront colors.' },
      { id: 'c', text: 'Time, location, officer identifiers, and what is being said or done.' },
      { id: 'd', text: 'Every rumor bystanders are repeating.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Useful records are concrete. Time, location, identifiers, and specific actions matter more than commentary or rumor.',
    reinforcement:
      'Capture facts that another person could verify later.',
    sourceRefs: ['communityWitnessing.documentSubtitle', 'communityWitnessing.doc1'],
    tags: ['witness', 'documentation'],
  },
  {
    id: 'witness-agents-approach-script',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'medium',
    prompt: 'If agents approach you while you are witnessing, what response is strongest?',
    choices: [
      { id: 'a', text: 'I am here as a witness. I am not interfering. I am documenting this from a safe distance.' },
      { id: 'b', text: 'You cannot talk to me because I know the Constitution.' },
      { id: 'c', text: 'I will stop recording if you explain your badge number.' },
      { id: 'd', text: 'I am going live right now unless you leave.' },
    ],
    correctChoiceId: 'a',
    explanation:
      'A witness needs language that is calm, plain, and hard to distort. Naming yourself as a witness and naming your distance keeps the record steady.',
    reinforcement:
      'Calm clarity protects better than dramatic language.',
    sourceRefs: ['communityWitnessing.agentsScript'],
    tags: ['witness', 'phrase'],
  },
  {
    id: 'witness-do-not-become-party',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'medium',
    prompt: 'Which action turns a witness into part of the confrontation?',
    choices: [
      { id: 'a', text: 'Recording from a safe distance.' },
      { id: 'b', text: 'Taking notes on time and location.' },
      { id: 'c', text: 'Stepping into the officers’ path and physically blocking movement.' },
      { id: 'd', text: 'Repeating what you directly observed.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'A witness preserves the record. Physical intervention changes your role and can collapse the boundary between observation and confrontation.',
    reinforcement:
      'Do not become the incident you were trying to document.',
    sourceRefs: ['communityWitnessing.dont1', 'communityWitnessing.witnessPartyBody'],
    tags: ['witness', 'boundaries'],
  },
  {
    id: 'witness-stoic-presence',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'easy',
    prompt: 'What kind of presence helps a witness most?',
    choices: [
      { id: 'a', text: 'A loud presence that dominates the encounter.' },
      { id: 'b', text: 'A disciplined presence that stays observant and hard to rattle.' },
      { id: 'c', text: 'A hidden presence that records nothing clearly.' },
      { id: 'd', text: 'An impulsive presence that reacts to every new detail.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A witness is most useful when their attention is steadier than the moment around them. Calm presence makes better memory, better video, and better testimony.',
    reinforcement:
      'Witnessing is a discipline before it becomes a record.',
    sourceRefs: ['communityWitnessing.stoicBody', 'communityWitnessing.stoicQuote'],
    tags: ['witness', 'presence'],
  },
  {
    id: 'witness-adrenaline-first-step',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'medium',
    prompt: 'If your own adrenaline spikes while witnessing, what is the best first correction?',
    choices: [
      { id: 'a', text: 'Move closer immediately so you feel useful.' },
      { id: 'b', text: 'Take one slower breath, widen your stance, and return to facts.' },
      { id: 'c', text: 'Start shouting so everyone knows you care.' },
      { id: 'd', text: 'Stop recording and improvise.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A witness is strongest when attention returns to the concrete. One slower breath and one steadier posture can keep the record from inheriting your panic.',
    reinforcement:
      'Come back to breath, stance, and facts before anything else.',
    sourceRefs: ['communityWitnessing.stoicBody', 'communityWitnessing.documentSubtitle'],
    tags: ['witness', 'calm', 'breathing'],
  },
  {
    id: 'witness-first-tool',
    type: 'multipleChoice',
    deck: legalQuizDecks.witnessing,
    category: 'witnessing',
    difficulty: 'easy',
    prompt: 'If there is time before officers notice you, what is the most useful first tool to start?',
    choices: [
      { id: 'a', text: 'A recording and a note of time and location.' },
      { id: 'b', text: 'A legal debate with nearby strangers.' },
      { id: 'c', text: 'A rumor chain in group chat.' },
      { id: 'd', text: 'A loud warning shouted from very close range.' },
    ],
    correctChoiceId: 'a',
    explanation:
      'A clear record often begins before the most dramatic part of the encounter. Time, location, and an early recording give the moment a structure that memory alone may not keep.',
    reinforcement:
      'Start the record before the scene has a chance to blur.',
    sourceRefs: ['communityWitnessing.doc1', 'communityWitnessing.doc2'],
    tags: ['witness', 'recording', 'documentation'],
  },
  {
    id: 'signals-one-meaning',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'easy',
    prompt: 'Why should each signal pattern have only one meaning?',
    choices: [
      { id: 'a', text: 'Because complicated systems look more serious.' },
      { id: 'b', text: 'Because one pattern should cover every possible emergency.' },
      { id: 'c', text: 'Because people need to recognize and act without guessing.' },
      { id: 'd', text: 'Because it is easier to remember the whistle itself than the meaning.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'A signal is useful only if the group can move on it immediately. Shared meaning matters more than cleverness.',
    reinforcement:
      'One pattern. One meaning. One response.',
    sourceRefs: ['signals.signalOnlyDesc'],
    tags: ['signals', 'protocol'],
  },
  {
    id: 'signals-lightest-first',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'easy',
    prompt: 'What is the best first move in a signal system?',
    choices: [
      { id: 'a', text: 'Use the most urgent signal immediately so no one misses it.' },
      { id: 'b', text: 'Start with the lightest recognizable pattern and escalate only if needed.' },
      { id: 'c', text: 'Use multiple signals at once so the crowd pays attention.' },
      { id: 'd', text: 'Skip signals and rely on shouting first.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Escalation works best when it is deliberate. Starting light keeps the group readable and leaves room to move upward only if the moment truly worsens.',
    reinforcement:
      'Notice first. Urgent only when urgency is real.',
    sourceRefs: ['signals.signal1Desc'],
    tags: ['signals', 'escalation'],
  },
  {
    id: 'signals-when-to-go-visual',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'medium',
    prompt: 'When should a group switch from sound to visible hand signals?',
    choices: [
      { id: 'a', text: 'Only after someone starts filming.' },
      { id: 'b', text: 'When noise is risky, the crowd is spread out, or spoken instructions will get lost.' },
      { id: 'c', text: 'Only indoors.' },
      { id: 'd', text: 'Only if the whistle is missing.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Visible coordination matters when sound could escalate the scene or fail to travel cleanly through the crowd.',
    reinforcement:
      'If sound raises risk, switch to something the eyes can carry.',
    sourceRefs: ['signals.handSignalsCardDescription'],
    tags: ['signals', 'visual'],
  },
  {
    id: 'signals-three-step-flow',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'medium',
    prompt: 'Which order matches the signal field sequence?',
    choices: [
      { id: 'a', text: 'Coordinate, alert, stabilize' },
      { id: 'b', text: 'Alert, coordinate, stabilize' },
      { id: 'c', text: 'Stabilize, alert, coordinate' },
      { id: 'd', text: 'Alert, stabilize, coordinate' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The group first needs attention, then coordination, then a calmer structure once people have oriented to the moment.',
    reinforcement:
      'Attention before coordination; coordination before stabilization.',
    sourceRefs: ['signals.communityTitle'],
    tags: ['signals', 'flow'],
  },
  {
    id: 'signals-digital-backup',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'medium',
    prompt: 'What is the role of digital backup in the signal system?',
    choices: [
      { id: 'a', text: 'Replace the need for shared in-person signals.' },
      { id: 'b', text: 'Support the main signal path with buddy systems and broadcast channels.' },
      { id: 'c', text: 'Make every alert public on the open internet.' },
      { id: 'd', text: 'Only store photos after the event is over.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Digital backup is a second layer, not the first move. It helps a group extend the alert or regroup without relying on one channel alone.',
    reinforcement:
      'The stronger system is the one with a backup path already agreed on.',
    sourceRefs: ['signals.telegramDesc', 'signals.buddyDesc'],
    tags: ['signals', 'comms'],
  },
  {
    id: 'signals-deescalation-after-alert',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'medium',
    prompt: 'Once attention lands, what should happen next?',
    choices: [
      { id: 'a', text: 'Keep increasing volume so urgency never drops.' },
      { id: 'b', text: 'Simplify movement, lower confusion, and shift the crowd into orientation.' },
      { id: 'c', text: 'Invent a new signal pattern on the spot.' },
      { id: 'd', text: 'Assume everyone understands the plan without further cues.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A signal system should not leave the crowd suspended in alarm. After attention comes the quieter work of turning it into readable action.',
    reinforcement:
      'The alert is not the endpoint. Orientation is.',
    sourceRefs: ['signals.dropVolumeDesc', 'signals.sitDownDesc'],
    tags: ['signals', 'deescalation'],
  },
  {
    id: 'signals-calm-after-pattern',
    type: 'multipleChoice',
    deck: legalQuizDecks.signals,
    category: 'signals',
    difficulty: 'medium',
    prompt: 'What helps keep an alert pattern from turning into panic?',
    choices: [
      { id: 'a', text: 'Adding a second urgent signal immediately whether or not it is needed.' },
      { id: 'b', text: 'Following the alert with visible, simple cues people can mirror.' },
      { id: 'c', text: 'Assuming the crowd already knows what to do.' },
      { id: 'd', text: 'Changing the meaning of the pattern midstream.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'An alert is only the opening move. People settle faster when the next cue is visible, simple, and shared enough to copy without guessing.',
    reinforcement:
      'After the alert, give the crowd something simple enough to mirror.',
    sourceRefs: ['signals.fieldSequenceTitle', 'signals.beforeYouRely1'],
    tags: ['signals', 'deescalation', 'visual'],
  },

  {
    id: 'unsafe-door-open',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'easy',
    prompt: 'Which response is the least safe?',
    choices: [
      { id: 'a', text: 'Keep the door closed and ask for a judge-signed warrant.' },
      { id: 'b', text: 'Open the door first so you can explain everything.' },
      { id: 'c', text: 'Say you do not consent to entry.' },
      { id: 'd', text: 'Request to speak to a lawyer.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Keeping the door closed preserves one of the strongest protections of the home. Opening it too soon gives that up.',
    reinforcement: 'Do not open the door just to explain or argue.',
    sourceRefs: ['scenarioData.doorStep1Explanation', 'scenarioData.doorOverview'],
    tags: ['unsafe-response', 'door'],
  },
  {
    id: 'unsafe-street-run',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'easy',
    prompt: 'Which response is the worst choice during a street stop?',
    choices: [
      { id: 'a', text: 'Ask if you are free to leave.' },
      { id: 'b', text: 'Run away when approached.' },
      { id: 'c', text: 'Remain calm and keep your hands visible.' },
      { id: 'd', text: 'Assert your right to remain silent.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Running escalates danger and can be used against you. Slow, clear rights assertions are safer.',
    reinforcement: 'Do not run. Slow down, stay calm, and use your words.',
    sourceRefs: ['scenarioData.streetKeyPoint4', 'scenarioData.streetBranch1aExplanation'],
    tags: ['unsafe-response', 'street'],
  },
  {
    id: 'unsafe-false-documents',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'medium',
    prompt: 'Which answer should you avoid?',
    choices: [
      { id: 'a', text: 'I am exercising my right to remain silent.' },
      { id: 'b', text: 'I want to speak to a lawyer.' },
      { id: 'c', text: 'Here are false papers to get this over with.' },
      { id: 'd', text: 'Am I free to leave?' },
    ],
    correctChoiceId: 'c',
    explanation:
      'False documents create new danger. A short, lawful assertion of rights is steadier and safer than deception.',
    reinforcement: 'Do not lie and do not provide false documents.',
    sourceRefs: ['scenarioData.streetKeyPoint5', 'scenarioData.borderStep2Explanation'],
    tags: ['unsafe-response', 'documents'],
  },
  {
    id: 'unsafe-signing',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'medium',
    prompt: 'Which response is unsafe if officers present paperwork?',
    choices: [
      { id: 'a', text: 'I will not sign any documents without my lawyer present.' },
      { id: 'b', text: 'I need my lawyer to review this first.' },
      { id: 'c', text: 'I will sign now and ask questions later.' },
      { id: 'd', text: 'I am not signing anything today.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Signing without legal review can close doors that are hard to reopen later.',
    reinforcement: 'Do not sign anything without a lawyer present.',
    sourceRefs: ['scenarioData.doorStep6Script', 'scenarioData.streetStep6Script', 'scenarioData.vehicleStep6Script'],
    tags: ['unsafe-response', 'signing'],
  },
  {
    id: 'unsafe-consent-language',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'medium',
    prompt: 'Which response weakens your position the most if someone wants to search?',
    choices: [
      { id: 'a', text: 'I do not consent to any searches.' },
      { id: 'b', text: 'I am not resisting, but I am not consenting.' },
      { id: 'c', text: 'Go ahead, I have nothing to hide.' },
      { id: 'd', text: 'I want to speak to a lawyer.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Once you consent, you give away the protection that a clear refusal was meant to preserve.',
    reinforcement: 'Never talk yourself into a search to seem cooperative.',
    sourceRefs: ['legal.fourthConsent', 'scenarioData.streetBranch3aScript'],
    tags: ['unsafe-response', 'searches'],
  },
  {
    id: 'unsafe-arguing-law',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'medium',
    prompt: 'Which approach goes against the safest encounter guidance?',
    choices: [
      { id: 'a', text: 'Assert your rights calmly and save the legal argument for later.' },
      { id: 'b', text: 'Use short phrases and keep repeating your rights.' },
      { id: 'c', text: 'Debate the law in detail on the scene until the officer gives up.' },
      { id: 'd', text: 'Request a lawyer and stay calm.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Short, repeatable rights assertions are safer than trying to win a legal argument in the moment.',
    reinforcement:
      'Use short rights language now; save the legal fight for court and counsel later.',
    sourceRefs: ['legal.heroScript1', 'legal.heroScript2', 'legal.heroScript3'],
    tags: ['unsafe-response', 'de-escalation'],
  },
  {
    id: 'unsafe-overexplaining',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'medium',
    prompt: 'Which response often becomes unsafe because fear keeps adding more words?',
    choices: [
      { id: 'a', text: 'I am exercising my right to remain silent.' },
      { id: 'b', text: 'I want to speak to a lawyer.' },
      { id: 'c', text: 'Let me explain everything from the beginning so you understand I belong here.' },
      { id: 'd', text: 'I do not consent to any searches.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Fear often tries to buy safety with explanation. In practice, long improvised stories can give away facts, guesses, and contradictions that a shorter rights-based response would not.',
    reinforcement:
      'Pressure usually asks for fewer words, not more.',
    sourceRefs: ['legal.fifthCanStaySilent', 'scenarioData.streetStep2Explanation'],
    tags: ['unsafe-response', 'silence', 'calm'],
  },
  {
    id: 'unsafe-sudden-movement',
    type: 'multipleChoice',
    deck: legalQuizDecks.unsafeResponses,
    category: 'unsafe-response',
    difficulty: 'easy',
    prompt: 'Which action creates unnecessary danger during a tense encounter?',
    choices: [
      { id: 'a', text: 'Keeping your hands visible.' },
      { id: 'b', text: 'Announcing before reaching for documents.' },
      { id: 'c', text: 'Making a quick unannounced reach into a pocket or glove box.' },
      { id: 'd', text: 'Slowing your breathing before answering.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Fast unexplained movement makes the moment harder to read. The safer path is visible hands, slower motion, and a short verbal warning before you reach.',
    reinforcement:
      'Let your movements be slower and easier to understand than the tension around you.',
    sourceRefs: ['scenarioData.vehicleStep3Script', 'scenarioData.streetKeyPoint2'],
    tags: ['unsafe-response', 'vehicle', 'calm'],
  },

  {
    id: 'phrase-recall-search',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'core-phrases',
    difficulty: 'easy',
    prompt: 'Tap the exact phrase used to refuse a search.',
    answerTokens: ['I', 'do', 'not', 'consent', 'to', 'any', 'searches.'],
    distractorTokens: ['maybe', 'later', 'if', 'quick', 'look', 'around'],
    explanation:
      'This phrase works because it is short, direct, and hard to misunderstand in a tense moment.',
    reinforcementPhrase: 'I do not consent to any searches.',
    sourceRefs: ['legal.fourthConsent', 'scenarioData.streetStep3Script', 'scenarioData.vehicleStep4Script'],
    tags: ['phrase', 'searches'],
  },
  {
    id: 'phrase-recall-leave',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'core-phrases',
    difficulty: 'easy',
    prompt: 'Tap the exact first street-stop question.',
    answerTokens: ['Am', 'I', 'free', 'to', 'leave?'],
    distractorTokens: ['why', 'are', 'you', 'stopping', 'me'],
    explanation:
      'This is the key first question in a public stop because it tells you whether the encounter is voluntary or a detention.',
    reinforcementPhrase: 'Am I free to leave?',
    sourceRefs: ['scenarioData.streetStep1Script', 'scenarioData.streetKeyPoint1'],
    tags: ['phrase', 'street'],
  },
  {
    id: 'phrase-recall-silence',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'core-phrases',
    difficulty: 'medium',
    prompt: 'Tap the exact phrase used to invoke silence in a simple, clear way.',
    answerTokens: ['I', 'am', 'exercising', 'my', 'right', 'to', 'remain', 'silent.'],
    distractorTokens: ['maybe', 'after', 'I', 'explain', 'everything'],
    explanation:
      'This wording matters because it is plain, direct, and leaves little room for someone else to reinterpret your intent.',
    reinforcementPhrase: 'I am exercising my right to remain silent.',
    sourceRefs: ['legal.fifthHowToAssert', 'scenarioData.workplaceStep2Script'],
    tags: ['phrase', 'silence'],
  },
  {
    id: 'phrase-recall-lawyer',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'core-phrases',
    difficulty: 'easy',
    prompt: 'Tap the exact phrase used to request a lawyer.',
    answerTokens: ['I', 'want', 'to', 'speak', 'to', 'a', 'lawyer.'],
    distractorTokens: ['maybe', 'call', 'someone', 'later', 'first'],
    explanation:
      'A short, unmistakable lawyer request is safer than vague language.',
    reinforcementPhrase: 'I want to speak to a lawyer.',
    sourceRefs: ['legal.fifthRequestLawyer', 'scenarioData.streetStep4Script', 'scenarioData.workplaceStep2Script'],
    tags: ['phrase', 'lawyer'],
  },
  {
    id: 'phrase-recall-door-script',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'door-phrase',
    difficulty: 'hard',
    prompt: 'Tap together the core door-rights phrase.',
    answerTokens: ['I', 'do', 'not', 'consent', 'to', 'you', 'entering', 'my', 'home.'],
    distractorTokens: ['come', 'inside', 'for', 'a', 'minute', 'please'],
    explanation:
      'At the door, direct non-consent language helps preserve the protection of the home.',
    reinforcementPhrase: 'I do not consent to you entering my home.',
    sourceRefs: ['scenarioData.doorStep4Script'],
    tags: ['phrase', 'door'],
  },
  {
    id: 'phrase-recall-no-sign',
    type: 'phraseRecall',
    deck: legalQuizDecks.phraseRecall,
    category: 'documents',
    difficulty: 'medium',
    prompt: 'Tap the exact phrase for refusing to sign paperwork.',
    answerTokens: ['I', 'will', 'not', 'sign', 'any', 'documents', 'without', 'my', 'lawyer', 'present.'],
    distractorTokens: ['after', 'this', 'later', 'maybe', 'review', 'quickly'],
    explanation:
      'Signing paperwork can waive rights in ways that are hard to undo later. This phrase creates pause before anything is signed in fear or confusion.',
    reinforcementPhrase: 'I will not sign any documents without my lawyer present.',
    sourceRefs: ['scenarioData.doorStep6Script', 'scenarioData.streetStep6Script', 'scenarioData.vehicleStep6Script'],
    tags: ['phrase', 'documents'],
  },

  // ── ICE ENCOUNTERS ────────────────────────────────────────────────────────
  {
    id: 'ice-door-first-move',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'easy',
    prompt: 'ICE knocks at your door. What is the first thing you should do?',
    choices: [
      { id: 'a', text: 'Open the door to see their credentials up close.' },
      { id: 'b', text: 'Keep the door closed and ask them to identify themselves and state their purpose through the door.' },
      { id: 'c', text: 'Immediately let them in so they see you have nothing to hide.' },
      { id: 'd', text: 'Run to a back room and ignore the knocking.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'You have the right to keep your door closed. Ask them to identify themselves and state their purpose without opening it. An open door can be treated as implied consent to enter.',
    reinforcement: 'Closed door = protected home. Ask through the door first.',
    sourceRefs: ['scenarioData.doorStep1Script', 'scenarioData.doorKeyPoint1'],
    tags: ['ice', 'door', '4th-amendment'],
  },
  {
    id: 'ice-administrative-warrant',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'medium',
    prompt: 'ICE shows an I-200 (administrative warrant) at your door. Does this allow them to enter your home?',
    choices: [
      { id: 'a', text: 'Yes — any official warrant requires you to open the door.' },
      { id: 'b', text: 'No — an administrative warrant is not signed by a judge and does not authorize entry into a home.' },
      { id: 'c', text: 'Only if two or more agents are present.' },
      { id: 'd', text: 'Yes, but only to the living room.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Administrative warrants (I-200 Warrant of Arrest, I-205 Warrant of Removal) are signed by an immigration official, not a federal judge. They do not authorize entry into a home. A judicial warrant signed by a judge is required.',
    reinforcement: 'I-200 / I-205 = administrative warrant. Does NOT authorize home entry.',
    sourceRefs: ['legal.fourthAdminVsJudicial', 'scenarioData.doorBranch3bExplanation'],
    tags: ['ice', 'warrant', '4th-amendment'],
  },
  {
    id: 'ice-right-to-silence',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'easy',
    prompt: 'If ICE asks about your immigration status or country of origin, are you required to answer?',
    choices: [
      { id: 'a', text: 'Yes, all people must answer immigration status questions.' },
      { id: 'b', text: 'Only U.S. citizens can stay silent; everyone else must answer.' },
      { id: 'c', text: 'No — you have the right to remain silent and can say: "I am exercising my right to remain silent."' },
      { id: 'd', text: 'Only if you have a lawyer physically present.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'The 5th Amendment right to remain silent applies to everyone in the U.S., regardless of immigration status. You are not required to answer questions about where you were born, how you entered, or your immigration status.',
    reinforcement: 'Everyone has the right to remain silent with ICE — citizen or not.',
    sourceRefs: ['legal.amendment5th', 'legal.fifthHowToAssert'],
    tags: ['ice', 'silence', '5th-amendment'],
  },
  {
    id: 'ice-detention-lawyer',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'easy',
    prompt: 'If ICE detains you, which right is critical to assert immediately?',
    choices: [
      { id: 'a', text: 'The right to negotiate the terms of your detention.' },
      { id: 'b', text: 'The right to speak to a lawyer before answering any questions.' },
      { id: 'c', text: 'The right to choose which detention facility you go to.' },
      { id: 'd', text: 'The right to call your employer first.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'If detained by ICE, immediately assert your right to speak to a lawyer. Do not sign anything and do not answer questions until you have counsel. Statements made before a lawyer arrives can be used against you.',
    reinforcement: 'Detained by ICE: request a lawyer immediately and do not sign anything.',
    sourceRefs: ['legal.amendment6th', 'legal.sixthRightToCounsel'],
    tags: ['ice', 'detention', 'lawyer', '6th-amendment'],
  },
  {
    id: 'ice-consent-entry',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'hard',
    prompt: 'ICE does not have a judicial warrant, but someone inside your home opens the door. Can they enter?',
    choices: [
      { id: 'a', text: 'No — only the homeowner can give consent.' },
      { id: 'b', text: 'Yes — opening the door by anyone inside can be interpreted as voluntary consent to enter.' },
      { id: 'c', text: 'No — opening the door is never considered consent.' },
      { id: 'd', text: 'Only if the person who opens the door is over 18.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Courts have found that voluntarily opening the door and allowing officers to step in can constitute implied consent to enter. This is why it is important not to open the door unless you see a valid judicial warrant.',
    reinforcement: 'Opening the door without a judicial warrant can be treated as consent to enter.',
    sourceRefs: ['legal.fourthYourHome', 'scenarioData.doorKeyPoint1'],
    tags: ['ice', 'door', 'consent', '4th-amendment'],
  },
  {
    id: 'ice-public-encounter',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'medium',
    prompt: 'ICE approaches you on the street. What is the first question to ask?',
    choices: [
      { id: 'a', text: '"What is your badge number?"' },
      { id: 'b', text: '"Am I free to leave?"' },
      { id: 'c', text: '"Why are you targeting me?"' },
      { id: 'd', text: '"Can I call my family first?"' },
    ],
    correctChoiceId: 'b',
    explanation:
      '"Am I free to leave?" determines whether you are being voluntarily questioned or detained. If they say yes, calmly walk away. If they say no, you are being detained and should assert your rights to silence and counsel.',
    reinforcement: 'Street ICE encounter first question: "Am I free to leave?"',
    sourceRefs: ['scenarioData.streetStep1Script', 'scenarioData.streetKeyPoint1'],
    tags: ['ice', 'street', 'detention'],
  },
  {
    id: 'ice-do-not-sign',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'medium',
    prompt: 'ICE presents papers and asks you to sign. What should you do?',
    choices: [
      { id: 'a', text: 'Sign quickly to speed up the process.' },
      { id: 'b', text: 'Sign only if the documents look official.' },
      { id: 'c', text: 'Refuse to sign anything until you have spoken with a lawyer.' },
      { id: 'd', text: 'Ask an officer to explain each document and then sign.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Signing immigration documents — especially a "voluntary departure" or removal order — can waive important rights and make it extremely difficult to challenge your case later. Never sign anything without talking to a lawyer first.',
    reinforcement: 'Never sign ICE documents without first consulting a lawyer.',
    sourceRefs: ['scenarioData.doorStep6Script', 'scenarioData.streetStep6Script'],
    tags: ['ice', 'documents', 'signing'],
  },
  {
    id: 'ice-workplace-non-public',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'medium',
    prompt: 'Can ICE enter the non-public back area of your workplace without a judicial warrant?',
    choices: [
      { id: 'a', text: 'Yes, because a workplace is not a home.' },
      { id: 'b', text: 'Yes, if at least one manager is present.' },
      { id: 'c', text: 'No — non-public work areas require a judicial warrant to enter, just like a home.' },
      { id: 'd', text: 'Only during normal business hours.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Non-public areas of a workplace — storage rooms, offices, break rooms — carry stronger privacy protections than public-facing areas. ICE needs a judicial warrant to enter those spaces.',
    reinforcement: 'Non-public workplace areas = same warrant requirement as a home.',
    sourceRefs: ['scenarioData.workplaceStep5Script', 'scenarioData.workplaceWarrantJudicialDesc'],
    tags: ['ice', 'workplace', 'warrant'],
  },
  {
    id: 'ice-14th-amendment',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'medium',
    prompt: 'Which amendment guarantees that due process protections apply to everyone in the U.S., including non-citizens?',
    choices: [
      { id: 'a', text: '1st Amendment' },
      { id: 'b', text: '4th Amendment' },
      { id: 'c', text: '6th Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'd',
    explanation:
      'The 14th Amendment says no state shall "deprive any person" — not "any citizen" — of life, liberty, or property without due process. Federal courts have applied this to ICE enforcement as well.',
    reinforcement: '14th Amendment due process applies to all persons, not only citizens.',
    sourceRefs: ['legal.amendment14th'],
    tags: ['ice', '14th-amendment', 'due-process'],
  },
  {
    id: 'ice-what-not-to-do',
    type: 'multipleChoice',
    deck: legalQuizDecks.iceEncounters,
    category: 'ice-encounter',
    difficulty: 'easy',
    prompt: 'Which of the following is most likely to make an ICE encounter worse?',
    choices: [
      { id: 'a', text: 'Staying calm, keeping hands visible, and asking short questions.' },
      { id: 'b', text: 'Physically running away or physically resisting being detained.' },
      { id: 'c', text: 'Invoking your right to remain silent.' },
      { id: 'd', text: 'Asking to see a judicial warrant.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Physically fleeing or resisting can result in additional criminal charges and injury. Rights are best asserted through calm, clear verbal statements — not physical action.',
    reinforcement: 'Never physically resist ICE. Assert rights verbally and calmly.',
    sourceRefs: ['scenarioData.streetStep1Script', 'scenarioData.doorBranch4aExplanation'],
    tags: ['ice', 'safety', 'calm'],
  },

  // ── TRAFFIC STOPS ─────────────────────────────────────────────────────────
  {
    id: 'traffic-pull-over-first',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'easy',
    prompt: 'When you see police lights behind you, what should you do first?',
    choices: [
      { id: 'a', text: 'Speed up slightly to find a safer spot before stopping.' },
      { id: 'b', text: 'Signal, pull over promptly and safely, turn off the engine, and place hands on the steering wheel.' },
      { id: 'c', text: 'Immediately open the door and step out to show you have nothing to hide.' },
      { id: 'd', text: 'Roll down all windows and put the car in park anywhere.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Signal, move to the right safely, stop promptly, turn off the ignition, and keep hands visible on the wheel. This reduces ambiguity and lowers tension before the officer even approaches.',
    reinforcement: 'Pull over safely, engine off, hands on the wheel where they can be seen.',
    sourceRefs: ['scenarioData.vehicleStep1Script'],
    tags: ['traffic-stop', 'vehicle', 'calm'],
  },
  {
    id: 'traffic-documents-required',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'easy',
    prompt: 'As the driver, which documents are you typically required to provide during a traffic stop?',
    choices: [
      { id: 'a', text: 'Driver license, vehicle registration, and proof of insurance.' },
      { id: 'b', text: 'Driver license and your full immigration history.' },
      { id: 'c', text: 'Just your name — you do not have to show paper documents.' },
      { id: 'd', text: 'Driver license, registration, and your phone unlocked.' },
    ],
    correctChoiceId: 'a',
    explanation:
      'Drivers are generally required to provide their license, registration, and proof of insurance. You are not required to hand over your phone or discuss immigration status.',
    reinforcement: 'Required: license, registration, insurance. Nothing more.',
    sourceRefs: ['scenarioData.vehicleStep3Script'],
    tags: ['traffic-stop', 'vehicle', 'documents'],
  },
  {
    id: 'traffic-announce-reach',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'easy',
    prompt: 'Before reaching for your license in the glove box during a traffic stop, you should:',
    choices: [
      { id: 'a', text: 'Reach quickly before the officer asks, so they see you are cooperative.' },
      { id: 'b', text: 'Tell the officer what you are about to do and then move slowly.' },
      { id: 'c', text: 'Keep both hands on the wheel and wait for the officer to take the documents themselves.' },
      { id: 'd', text: 'Reach and grab everything in the glove box at once.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Always announce your movement before reaching. "My license is in the glove box — may I reach for it?" removes ambiguity and reduces misinterpretation of sudden movement.',
    reinforcement: 'Say what you are about to reach for before your hands move.',
    sourceRefs: ['scenarioData.vehicleStep3Script', 'scenarioData.vehicleKeyPoint3'],
    tags: ['traffic-stop', 'vehicle', 'calm', 'documents'],
  },
  {
    id: 'traffic-search-consent',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'easy',
    prompt: 'An officer asks to search your car during a traffic stop. What should you say?',
    choices: [
      { id: 'a', text: '"Sure, go ahead" — saying yes speeds up the stop.' },
      { id: 'b', text: '"I do not consent to any searches of my vehicle."' },
      { id: 'c', text: 'Say nothing and hope they do not search.' },
      { id: 'd', text: '"I consent, but only to the front seat."' },
    ],
    correctChoiceId: 'b',
    explanation:
      'You have the right to decline a vehicle search. A clear verbal refusal preserves your 4th Amendment rights. If the officer searches anyway without legal grounds, your refusal protects your legal position.',
    reinforcement: 'Always refuse a consent search clearly: "I do not consent to any searches of my vehicle."',
    sourceRefs: ['scenarioData.vehicleBranch3aScript', 'legal.fourthConsent'],
    tags: ['traffic-stop', 'vehicle', 'searches', '4th-amendment'],
  },
  {
    id: 'traffic-without-consent-search',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'medium',
    prompt: 'Without your consent, when can police legally search your vehicle?',
    choices: [
      { id: 'a', text: 'Anytime during any traffic stop.' },
      { id: 'b', text: 'Never — consent is always required.' },
      { id: 'c', text: 'With a warrant, if contraband is in plain view, or if they have probable cause.' },
      { id: 'd', text: 'Only if you have a prior criminal record.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Police can search a vehicle without consent if they have a warrant, if contraband is in plain view, or if they have probable cause to believe evidence of a crime is present. That is why it is important not to give consent and to keep contraband out of sight.',
    reinforcement: 'Warrant, plain view, or probable cause — the three exceptions to consent.',
    sourceRefs: ['legal.amendment4th', 'scenarioData.vehicleWarrantAdminDesc'],
    tags: ['traffic-stop', 'vehicle', 'searches', 'warrant', '4th-amendment'],
  },
  {
    id: 'traffic-phone-search',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'medium',
    prompt: 'During a traffic stop, an officer asks to look through your phone. What protection applies?',
    choices: [
      { id: 'a', text: 'None — a traffic stop gives officers authority over all items in the car.' },
      { id: 'b', text: 'The Supreme Court ruled in Riley v. California that police generally need a warrant to search your phone.' },
      { id: 'c', text: 'You must unlock it if you are the driver.' },
      { id: 'd', text: 'Only passwords are protected, not the phone itself.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'In Riley v. California (2014), the Supreme Court held that cell phones contain so much personal data that police must generally get a warrant before searching them, even during an arrest.',
    reinforcement: 'Phone search requires a warrant — Riley v. California, 2014.',
    sourceRefs: ['legal.amendment4th'],
    tags: ['traffic-stop', 'phone', 'warrant', '4th-amendment'],
  },
  {
    id: 'traffic-exit-vehicle',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'medium',
    prompt: 'If an officer orders you to exit your vehicle, what should you do?',
    choices: [
      { id: 'a', text: 'Refuse because officers cannot order you out during a traffic stop.' },
      { id: 'b', text: 'Comply calmly, step out slowly, and keep your hands visible.' },
      { id: 'c', text: 'Argue the reason before agreeing to step out.' },
      { id: 'd', text: 'Immediately call 911 to report the order.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Under Pennsylvania v. Mimms, officers may order a driver out of a vehicle during a lawful traffic stop as a matter of officer safety. Complying calmly while keeping hands visible is the safest response.',
    reinforcement: 'Exit orders are lawful. Comply calmly, hands visible.',
    sourceRefs: ['scenarioData.vehicleStep2Script'],
    tags: ['traffic-stop', 'vehicle', 'exit', 'calm'],
  },
  {
    id: 'traffic-passenger-id',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'hard',
    prompt: 'As a passenger during a traffic stop, are you required to show ID?',
    choices: [
      { id: 'a', text: 'Yes, in every state passengers must show ID.' },
      { id: 'b', text: 'No — passengers are never required to show ID.' },
      { id: 'c', text: 'It depends on the state, and you can ask "Am I free to leave?" to clarify your status.' },
      { id: 'd', text: 'Only if the driver is arrested.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Passenger ID requirements vary by state. In most states, passengers are not required to provide ID unless there is independent reasonable suspicion they are involved in criminal activity. Asking "Am I free to leave?" is the right first move.',
    reinforcement: 'Passenger ID rules vary by state. Ask "Am I free to leave?" first.',
    sourceRefs: ['scenarioData.vehicleBranch2bScript', 'scenarioData.vehicleBranch2bExplanation'],
    tags: ['traffic-stop', 'vehicle', 'passenger'],
  },
  {
    id: 'traffic-record-stop',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'medium',
    prompt: 'Can you record a traffic stop from inside your own car?',
    choices: [
      { id: 'a', text: 'No — recording inside a vehicle is always prohibited.' },
      { id: 'b', text: 'Yes — the 1st Amendment generally protects the right to record police in public, including from inside your car.' },
      { id: 'c', text: 'Only if you tell the officer before you start.' },
      { id: 'd', text: 'Only in states that have passed specific recording laws.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Recording police during a traffic stop from inside your vehicle is generally protected by the 1st Amendment as long as you are not interfering with the stop. Place your phone visibly on the dash to record without creating extra tension.',
    reinforcement: '1st Amendment generally protects recording police — including from inside your car.',
    sourceRefs: ['legal.amendment1st', 'legal.recordingYouCanRecord'],
    tags: ['traffic-stop', 'recording', '1st-amendment'],
  },
  {
    id: 'traffic-racial-profiling',
    type: 'multipleChoice',
    deck: legalQuizDecks.trafficStops,
    category: 'traffic-stop',
    difficulty: 'medium',
    prompt: 'If you believe you were stopped because of your race, what is the best immediate response?',
    choices: [
      { id: 'a', text: 'Refuse to cooperate until the officer explains the real reason.' },
      { id: 'b', text: 'Comply with the stop, document everything, and file a complaint through proper channels afterward.' },
      { id: 'c', text: 'Tell the officer that you know it is racial profiling.' },
      { id: 'd', text: 'Drive away and report it later.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The safest and most effective approach is to comply with lawful orders during the stop, note the officer\'s name/badge number, the time and location, and file a formal complaint or consult an attorney afterward.',
    reinforcement: 'Document everything during; challenge it afterward through proper channels.',
    sourceRefs: ['legal.amendment14th'],
    tags: ['traffic-stop', 'profiling', '14th-amendment'],
  },

  // ── PROTEST RIGHTS ────────────────────────────────────────────────────────
  {
    id: 'protest-1st-protection',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'easy',
    prompt: 'Which amendment most directly protects the right to peacefully protest in public?',
    choices: [
      { id: 'a', text: '4th Amendment' },
      { id: 'b', text: '5th Amendment' },
      { id: 'c', text: '1st Amendment' },
      { id: 'd', text: '14th Amendment' },
    ],
    correctChoiceId: 'c',
    explanation:
      'The 1st Amendment protects freedom of speech, peaceful assembly, and petitioning the government. Peaceful protests in public spaces are among its core protections.',
    reinforcement: '1st Amendment = speech, assembly, protest.',
    sourceRefs: ['legal.amendment1st', 'legal.rightToProtestAssemble'],
    tags: ['protest', '1st-amendment'],
  },
  {
    id: 'protest-permit-required',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'When might a permit be required for a protest?',
    choices: [
      { id: 'a', text: 'Never — protests are always permit-free under the 1st Amendment.' },
      { id: 'b', text: 'For large marches that use streets or parks, or events using amplified sound.' },
      { id: 'c', text: 'Only for protests with more than 1,000 people.' },
      { id: 'd', text: 'Only for protests on private property.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Cities may require permits for events that use roads, parks, or amplified sound. However, spontaneous protests in response to breaking news events are generally exempt. Small gatherings on public sidewalks usually need no permit.',
    reinforcement: 'Large marches and amplified events may need permits. Spontaneous protests are usually exempt.',
    sourceRefs: ['legal.protestPeacefulAssembly'],
    tags: ['protest', '1st-amendment', 'permit'],
  },
  {
    id: 'protest-recording-police',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'easy',
    prompt: 'Can police order you to stop recording them at a protest?',
    choices: [
      { id: 'a', text: 'Yes — they can always order you to stop recording.' },
      { id: 'b', text: 'No — recording officers in public is generally protected by the 1st Amendment as long as you do not interfere.' },
      { id: 'c', text: 'Only if you are more than 20 feet away.' },
      { id: 'd', text: 'Only journalists can record; bystanders cannot.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Multiple federal appeals courts have affirmed that recording police in public is a 1st Amendment right. Officers may not order you to stop recording solely because they do not want to be filmed, as long as you are not physically interfering.',
    reinforcement: 'Recording police at a protest is protected — do not interfere, just document.',
    sourceRefs: ['legal.amendment1st', 'legal.recordingYouCanRecord'],
    tags: ['protest', 'recording', '1st-amendment'],
  },
  {
    id: 'protest-dispersal-order',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'Police issue a dispersal order at a protest. What should you do?',
    choices: [
      { id: 'a', text: 'Ignore it — dispersal orders are always unconstitutional.' },
      { id: 'b', text: 'Comply promptly to avoid arrest, and note the time and what officers said.' },
      { id: 'c', text: 'Link arms and hold your ground until they use force.' },
      { id: 'd', text: 'Start a counter-chant to show you know your rights.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'If police declare an unlawful assembly and order dispersal, staying puts you at risk of arrest regardless of whether the order is lawful. The safer choice is to leave, document everything, and challenge unlawful orders through the courts afterward.',
    reinforcement: 'Comply with dispersal orders in the moment; challenge them legally afterward.',
    sourceRefs: ['legal.protestPeacefulAssembly'],
    tags: ['protest', 'dispersal', 'arrest'],
  },
  {
    id: 'protest-arrest-response',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'easy',
    prompt: 'If you are arrested at a protest, what is the most important thing to do?',
    choices: [
      { id: 'a', text: 'Explain your side of the story to the arresting officers right away.' },
      { id: 'b', text: 'Stay calm, do not physically resist, and clearly state: "I want a lawyer."' },
      { id: 'c', text: 'Give a false name to protect yourself.' },
      { id: 'd', text: 'Call out to the crowd to document your arrest.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Physical resistance adds charges and risk. Clearly invoking your right to counsel — "I want a lawyer" — is the single most protective thing you can do. Anything said before a lawyer is present can be used against you.',
    reinforcement: 'Arrested at a protest: stay calm, do not resist, demand a lawyer.',
    sourceRefs: ['legal.amendment6th', 'legal.sixthRightToCounsel'],
    tags: ['protest', 'arrest', '6th-amendment', 'lawyer'],
  },
  {
    id: 'protest-counter-protesters',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'Do counter-protesters have 1st Amendment protections at the same location?',
    choices: [
      { id: 'a', text: 'No — only the original permitted group has rights at that location.' },
      { id: 'b', text: 'Yes — counter-protesters also have the right to be present and express their views as long as they do not incite violence.' },
      { id: 'c', text: 'Only if police approve the counter-protest.' },
      { id: 'd', text: 'Counter-protests are never protected.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Counter-protesters have 1st Amendment rights too. Police must generally allow both groups to demonstrate, but may keep them separated. Counter-protesters may not incite violence or physically disrupt the other event.',
    reinforcement: 'Counter-protesters have 1st Amendment rights, but may not incite violence.',
    sourceRefs: ['legal.amendment1st', 'legal.protestPeacefulAssembly'],
    tags: ['protest', '1st-amendment'],
  },
  {
    id: 'protest-write-number-on-arm',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'easy',
    prompt: 'Before going to a protest, what is a common safety practice for legal emergencies?',
    choices: [
      { id: 'a', text: 'Bring a printed copy of the Constitution.' },
      { id: 'b', text: 'Write a legal support hotline number in permanent marker on your arm.' },
      { id: 'c', text: 'Memorize every officer\'s name in the local precinct.' },
      { id: 'd', text: 'Create a separate social media account for the event.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'If you are arrested, your phone may be confiscated and you may have no way to reach a lawyer. Writing a legal support hotline number on your arm ensures you can always call for help.',
    reinforcement: 'Write a legal support number on your arm — your phone may be taken.',
    sourceRefs: [],
    tags: ['protest', 'preparation', 'legal-support'],
  },
  {
    id: 'protest-unlawful-assembly',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'What typically transforms a lawful assembly into an "unlawful assembly"?',
    choices: [
      { id: 'a', text: 'Any gathering of more than 10 people without a permit.' },
      { id: 'b', text: 'When the group presents a clear and present danger through violence or imminent lawless action.' },
      { id: 'c', text: 'Any protest that blocks foot traffic.' },
      { id: 'd', text: 'Whenever police decide to make arrests.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A lawful assembly becomes unlawful when participants turn violent, create imminent danger, or an officer issues a lawful dispersal order and people refuse to leave. Peaceful assembly does not become unlawful simply because police feel inconvenienced.',
    reinforcement: 'Violence or imminent danger, not inconvenience, is what makes an assembly unlawful.',
    sourceRefs: ['legal.protestPeacefulAssembly', 'legal.speechNotProtected'],
    tags: ['protest', '1st-amendment', 'dispersal'],
  },
  {
    id: 'protest-protect-identity',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'Which preparation best protects your privacy at a protest?',
    choices: [
      { id: 'a', text: 'Leave your phone at home entirely.' },
      { id: 'b', text: 'Use airplane mode and enable a strong lock screen PIN so police cannot access your phone if seized.' },
      { id: 'c', text: 'Wear a mask so you cannot be photographed.' },
      { id: 'd', text: 'Post your location live so witnesses can track you.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'A strong device PIN and knowing you can invoke your right not to unlock it (Riley v. California) protects your data. A PIN is more secure than biometric unlock in law-enforcement contexts, as courts are divided on whether officers can compel biometric unlock.',
    reinforcement: 'Strong PIN on your phone. Airplane mode during a protest protects your data.',
    sourceRefs: ['legal.amendment4th'],
    tags: ['protest', 'phone', 'privacy', '4th-amendment'],
  },
  {
    id: 'protest-legal-observer',
    type: 'multipleChoice',
    deck: legalQuizDecks.protestRights,
    category: 'protest',
    difficulty: 'medium',
    prompt: 'What is the role of a legal observer at a protest?',
    choices: [
      { id: 'a', text: 'To act as a lawyer who can get you released on the spot.' },
      { id: 'b', text: 'To document what happens at the protest as a neutral witness who can provide evidence if arrests are challenged.' },
      { id: 'c', text: 'To form a barrier between police and protesters.' },
      { id: 'd', text: 'To negotiate with officers on behalf of the group.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Legal observers — often trained by organizations like the National Lawyers Guild — document police conduct and protester behavior as neutral witnesses. Their notes and recordings can become evidence in legal challenges.',
    reinforcement: 'Legal observers document; they do not intervene directly.',
    sourceRefs: [],
    tags: ['protest', 'witness', 'documentation'],
  },

  // ── FIRST AID ─────────────────────────────────────────────────────────────
  {
    id: 'firstaid-bleeding-control',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'easy',
    prompt: 'Someone has a severe, actively bleeding wound. What is the first priority?',
    choices: [
      { id: 'a', text: 'Rinse the wound with water before doing anything.' },
      { id: 'b', text: 'Apply firm, direct pressure to the wound and do not release it.' },
      { id: 'c', text: 'Elevate the limb and wait for EMS.' },
      { id: 'd', text: 'Apply a tourniquet immediately as the very first step for any bleeding wound.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Direct, firm, continuous pressure is the first step for severe bleeding. Do not remove the cloth once applied — if it soaks through, add more material on top. Elevation helps but is secondary. Tourniquets are for life-threatening limb bleeding that cannot be controlled by direct pressure.',
    reinforcement: 'Direct pressure first. Do not lift the cloth. Add on top if needed.',
    sourceRefs: [],
    tags: ['first-aid', 'bleeding'],
  },
  {
    id: 'firstaid-heat-stroke-vs-exhaustion',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'At an outdoor summer demonstration, someone suddenly stops moving and collapses. Which signs most clearly indicate heat stroke — a life-threatening emergency — rather than heat exhaustion?',
    choices: [
      { id: 'a', text: 'Heavy sweating and muscle cramps.' },
      { id: 'b', text: 'Confusion or unresponsiveness, hot dry or flushed skin, and body temperature above 103°F.' },
      { id: 'c', text: 'Dizziness and nausea with cool, pale, clammy skin.' },
      { id: 'd', text: 'Fatigue and dark-colored urine.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Heat stroke is a life-threatening emergency requiring immediate 911 response. Key signs are altered mental status (confusion, unresponsiveness), skin that is hot and dry (no longer sweating), and very high body temperature. Heat exhaustion involves heavy sweating and cool, pale skin — serious but not immediately life-threatening. At outdoor protests, move the person to shade, apply cool water or ice, and call 911.',
    reinforcement: 'Confusion + hot dry skin at a protest = heat stroke → shade, cool water, call 911.',
    sourceRefs: [],
    tags: ['first-aid', 'heat', 'protest'],
  },
  {
    id: 'firstaid-cpr-rate',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'What is the recommended chest compression rate for adult CPR?',
    choices: [
      { id: 'a', text: '60–80 compressions per minute.' },
      { id: 'b', text: '100–120 compressions per minute.' },
      { id: 'c', text: '140–160 compressions per minute.' },
      { id: 'd', text: '50–60 compressions per minute, slow and deep.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Current American Heart Association guidelines recommend 100–120 chest compressions per minute — roughly the beat of the song "Stayin\' Alive." Compress at least 2 inches deep and allow full chest recoil between compressions.',
    reinforcement: 'CPR rate: 100–120 per minute. "Stayin\' Alive" tempo.',
    sourceRefs: [],
    tags: ['first-aid', 'cpr'],
  },
  {
    id: 'firstaid-good-samaritan',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'easy',
    prompt: 'You want to help an injured person at a demonstration, but you worry about being sued if your first aid causes unintended harm. What legal protection likely applies?',
    choices: [
      { id: 'a', text: 'No protection exists — you are fully liable for any outcome when you intervene.' },
      { id: 'b', text: 'Good Samaritan laws in all 50 U.S. states generally protect bystanders who provide reasonable emergency assistance in good faith from civil liability.' },
      { id: 'c', text: 'Protection only applies to licensed medical professionals.' },
      { id: 'd', text: 'You are protected only if you call 911 before you help.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Good Samaritan laws — enacted in all 50 U.S. states — generally shield people who render emergency first aid in good faith from civil lawsuits. They do not protect gross negligence or intentional harm. Knowing this protection exists removes a major barrier to helping injured community members in the field.',
    reinforcement: 'Good Samaritan laws protect bystander first aid in all 50 states. Help without fear of lawsuit.',
    sourceRefs: [],
    tags: ['first-aid', 'good-samaritan', 'legal', 'liability'],
  },
  {
    id: 'firstaid-seizure',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'easy',
    prompt: 'Someone is having a seizure. Which action is correct?',
    choices: [
      { id: 'a', text: 'Hold them still so they do not injure themselves.' },
      { id: 'b', text: 'Put something in their mouth to protect their tongue.' },
      { id: 'c', text: 'Cushion their head, clear the area of hard objects, and time the seizure.' },
      { id: 'd', text: 'Give them water once the shaking slows.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Do not restrain the person or put anything in their mouth (people cannot swallow their tongues and you risk injury to both of you). Cushion the head, move hard objects away, turn them on their side if possible, and time it — call 911 if it lasts more than 5 minutes.',
    reinforcement: 'Seizure: cushion head, clear space, time it. Nothing in the mouth.',
    sourceRefs: [],
    tags: ['first-aid', 'seizure'],
  },
  {
    id: 'firstaid-overdose-naloxone',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'You suspect someone is experiencing an opioid overdose. They are unconscious and barely breathing. What should you do?',
    choices: [
      { id: 'a', text: 'Put them in a cold shower to wake them up.' },
      { id: 'b', text: 'Call 911, administer naloxone (Narcan) if available, and perform rescue breathing.' },
      { id: 'c', text: 'Wait to see if they improve on their own before calling 911.' },
      { id: 'd', text: 'Give them stimulants to counteract the opioid.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Call 911 immediately. Naloxone (Narcan) reverses opioid overdose and is available without a prescription in many states. Give it nasally or by injection, then perform rescue breathing if needed. Do not leave the person alone — a second dose may be needed.',
    reinforcement: 'Opioid OD: 911 + naloxone + rescue breathing. Do not leave them alone.',
    sourceRefs: [],
    tags: ['first-aid', 'overdose', 'naloxone'],
  },
  {
    id: 'firstaid-shock-signs',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'Which combination of signs most suggests someone is going into shock?',
    choices: [
      { id: 'a', text: 'Hot, flushed skin and a slow, strong pulse.' },
      { id: 'b', text: 'Pale, cold, clammy skin; rapid weak pulse; rapid breathing; and confusion or anxiety.' },
      { id: 'c', text: 'Normal skin color with slow, labored breathing.' },
      { id: 'd', text: 'Bright red skin and nausea only.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Shock occurs when the body cannot get enough blood to vital organs. Classic signs: pale, cold, clammy skin; rapid, weak pulse; fast shallow breathing; confusion. Lay the person flat, keep them warm, and call 911.',
    reinforcement: 'Shock: pale + cold + clammy skin, weak fast pulse. Lay flat, call 911.',
    sourceRefs: [],
    tags: ['first-aid', 'shock'],
  },
  {
    id: 'firstaid-tear-gas',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'You or someone nearby has been exposed to tear gas. What is the most important first step?',
    choices: [
      { id: 'a', text: 'Rub the eyes and face vigorously to remove the irritant.' },
      { id: 'b', text: 'Move upwind and to fresh air, then flush eyes with large amounts of clean water or saline.' },
      { id: 'c', text: 'Apply milk of magnesia to the face immediately.' },
      { id: 'd', text: 'Keep eyes closed and avoid water, which activates the agent.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Move to fresh air upwind of the gas cloud. For eyes: hold eyelids open and flush with large amounts of clean water or sterile saline for 10–15 minutes. Do not rub — rubbing spreads the agent. Remove contaminated clothing. Blinking helps flush the agent naturally.',
    reinforcement: 'Tear gas: upwind + fresh air → flush eyes with water. Do not rub.',
    sourceRefs: [],
    tags: ['first-aid', 'tear-gas', 'protest'],
  },
  {
    id: 'firstaid-recovery-position',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'easy',
    prompt: 'When should you place someone in the recovery position (on their side)?',
    choices: [
      { id: 'a', text: 'Any time they are sitting down and feel dizzy.' },
      { id: 'b', text: 'When they are unconscious but breathing, to prevent choking on vomit or fluids.' },
      { id: 'c', text: 'Only after CPR has been started.' },
      { id: 'd', text: 'Whenever someone has a suspected spinal injury.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'The recovery position is used for someone who is unconscious but breathing normally. It keeps the airway open and prevents choking. Do not use it if you suspect a spinal injury — in that case, minimize movement and wait for EMS.',
    reinforcement: 'Unconscious but breathing → recovery position to protect the airway.',
    sourceRefs: [],
    tags: ['first-aid', 'airway'],
  },
  {
    id: 'firstaid-pepper-spray',
    type: 'multipleChoice',
    deck: legalQuizDecks.firstAid,
    category: 'first-aid',
    difficulty: 'medium',
    prompt: 'A community member near you is hit with OC spray (pepper spray) during a police encounter. What is the correct first response?',
    choices: [
      { id: 'a', text: 'Rub the affected area vigorously to remove the oil faster.' },
      { id: 'b', text: 'Move upwind to fresh air, have them blink rapidly, and flush eyes and skin with large amounts of cool water for 10–15 minutes.' },
      { id: 'c', text: 'Apply milk to the eyes immediately to neutralize the chemical.' },
      { id: 'd', text: 'Keep eyes shut and wait — the burning will pass on its own in a few minutes.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'OC spray is an oil-based irritant. Rubbing spreads it and makes it worse. Move upwind, away from the cloud. Have the person blink rapidly to produce tears, then flush eyes and skin with cool water for at least 10–15 minutes. Remove and bag contaminated clothing. Avoid touching the face with contaminated hands.',
    reinforcement: 'OC spray: upwind + blink + flush with water. Never rub. Remove contaminated clothing.',
    sourceRefs: [],
    tags: ['first-aid', 'pepper-spray', 'oc-spray', 'protest'],
  },

  // ── DE-ESCALATION ─────────────────────────────────────────────────────────
  {
    id: 'deescalation-first-step',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'easy',
    prompt: 'When an encounter begins to feel tense, what is the most effective first de-escalation step?',
    choices: [
      { id: 'a', text: 'Speak louder and more firmly to establish control.' },
      { id: 'b', text: 'Slow your own breathing, lower your voice, and create a little physical space.' },
      { id: 'c', text: 'Immediately involve more bystanders to increase social pressure.' },
      { id: 'd', text: 'Mirror the other person\'s energy to show empathy.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Your own nervous system is contagious. Slowing your breath, dropping your voice, and adding space signals safety instead of threat — which gives the other person\'s nervous system a chance to downshift too.',
    reinforcement: 'Slow breath + quiet voice + space. Your calm is contagious.',
    sourceRefs: [],
    tags: ['de-escalation', 'calm', 'breathing'],
  },
  {
    id: 'deescalation-body-language',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'easy',
    prompt: 'Which body language posture is most helpful when trying to de-escalate someone?',
    choices: [
      { id: 'a', text: 'Arms crossed, direct eye contact, standing close.' },
      { id: 'b', text: 'Hands visible and relaxed, slight side angle (not square-on), calm steady eye contact.' },
      { id: 'c', text: 'Standing as tall as possible to project authority.' },
      { id: 'd', text: 'Turning away so the person does not feel watched.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Open hands signal non-threat. A slight side angle (instead of squaring up) reduces the confrontational read. Calm, steady eye contact — not staring — communicates presence and respect at the same time.',
    reinforcement: 'Open hands, slight side angle, calm gaze. Not squared up.',
    sourceRefs: [],
    tags: ['de-escalation', 'body-language'],
  },
  {
    id: 'deescalation-phrase-to-avoid',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'Which phrase most commonly backfires when someone is already angry?',
    choices: [
      { id: 'a', text: '"I hear that you\'re upset."' },
      { id: 'b', text: '"Calm down."' },
      { id: 'c', text: '"Help me understand what happened."' },
      { id: 'd', text: '"What would help right now?"' },
    ],
    correctChoiceId: 'b',
    explanation:
      '"Calm down" tells someone how they should feel instead of acknowledging how they do feel. It typically increases frustration because it is dismissive. Phrases that acknowledge feeling and invite explanation are far more likely to lower the temperature.',
    reinforcement: 'Never say "calm down." Acknowledge the feeling instead.',
    sourceRefs: [],
    tags: ['de-escalation', 'language'],
  },
  {
    id: 'deescalation-active-listening',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'What does active listening look like in a tense encounter?',
    choices: [
      { id: 'a', text: 'Nodding rapidly and looking for a gap to respond.' },
      { id: 'b', text: 'Repeating back what you heard in your own words to check understanding before responding.' },
      { id: 'c', text: 'Staying quiet and waiting for the person to run out of things to say.' },
      { id: 'd', text: 'Making eye contact while planning your counterargument.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Active listening means reflecting back what you heard — "So what I\'m hearing is..." — before responding. This confirms understanding, shows respect, and slows the conversation enough that each side can actually process what the other is saying.',
    reinforcement: 'Reflect what you heard before you respond. Check before you correct.',
    sourceRefs: [],
    tags: ['de-escalation', 'listening'],
  },
  {
    id: 'deescalation-offer-choices',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'Why does offering someone a choice — even a small one — help de-escalate a confrontation?',
    choices: [
      { id: 'a', text: 'It confuses them long enough to leave.' },
      { id: 'b', text: 'It restores a sense of control, which reduces threat response and defensive behavior.' },
      { id: 'c', text: 'It shifts blame for the situation to them.' },
      { id: 'd', text: 'Choices are only useful if the person is completely calm.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Much of what drives escalation is feeling trapped or powerless. A small, genuine choice — "Would you prefer to talk here or over there?" — restores agency and signals that the encounter is collaborative, not coercive.',
    reinforcement: 'Offering a choice restores agency and lowers the threat response.',
    sourceRefs: [],
    tags: ['de-escalation', 'agency'],
  },
  {
    id: 'deescalation-bystander',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'As a bystander watching a verbal confrontation escalate, which action is most likely to help?',
    choices: [
      { id: 'a', text: 'Shout at the escalating party to make them stop.' },
      { id: 'b', text: 'Step in close to both parties to show you are there.' },
      { id: 'c', text: 'Calmly address the calmer of the two parties first, or gently redirect attention away from the flashpoint.' },
      { id: 'd', text: 'Do nothing until one party requests your help.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'Engaging the calmer party, or offering a distraction (asking a simple question, offering water) shifts the focus without directly challenging the escalating party. Shouting or crowding in tends to increase stimulation rather than reduce it.',
    reinforcement: 'Engage the calmer party or gently redirect. Do not match the energy.',
    sourceRefs: [],
    tags: ['de-escalation', 'bystander'],
  },
  {
    id: 'deescalation-mental-health-crisis',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'Someone in your community is in a mental health crisis — agitated but not posing an immediate danger. What is the safest first step?',
    choices: [
      { id: 'a', text: 'Call 911 immediately so professionals can handle it.' },
      { id: 'b', text: 'Approach in a group to show community support.' },
      { id: 'c', text: 'Give them space, speak calmly in a low voice, and ask simple open-ended questions.' },
      { id: 'd', text: 'Restrain them gently until help arrives.' },
    ],
    correctChoiceId: 'c',
    explanation:
      'For someone in a mental health crisis who is not immediately dangerous: space, low calm voice, and simple open-ended questions (not yes/no) help them orient and feel less overwhelmed. Sending a crowd, physical restraint, or an immediate 911 call can all escalate a non-violent situation.',
    reinforcement: 'Mental health crisis: space, low voice, simple open questions. Avoid crowds.',
    sourceRefs: [],
    tags: ['de-escalation', 'mental-health'],
  },
  {
    id: 'deescalation-when-to-leave',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'When is leaving the encounter the correct de-escalation choice?',
    choices: [
      { id: 'a', text: 'Only if you have tried every verbal technique and failed.' },
      { id: 'b', text: 'When the person shows signs they may become physically violent, and staying puts you or others at risk.' },
      { id: 'c', text: 'Only after you have asked permission to leave.' },
      { id: 'd', text: 'Leaving is always giving up — stay until it is resolved.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'De-escalation includes knowing when not to engage. If someone is escalating toward physical violence and your presence is raising the risk, the most protective action is to create distance and call for trained help if needed.',
    reinforcement: 'If physical violence feels imminent, leaving IS the de-escalation.',
    sourceRefs: [],
    tags: ['de-escalation', 'safety'],
  },
  {
    id: 'deescalation-911-alternatives',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'medium',
    prompt: 'For a mental health crisis that is not immediately dangerous, what is an alternative to calling 911?',
    choices: [
      { id: 'a', text: 'There is no alternative — 911 is the only emergency resource.' },
      { id: 'b', text: 'Mobile crisis teams, warmlines, and community responder programs, where available.' },
      { id: 'c', text: 'Wait until a doctor is present before doing anything.' },
      { id: 'd', text: 'Call the person\'s family without their consent.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Many cities now have mobile mental health crisis teams or community responder programs that can respond without armed police — reducing the risk of escalation for people in psychiatric crises. Examples include CAHOOTS (Eugene, OR), Denver STAR, and the 988 Suicide and Crisis Lifeline.',
    reinforcement: 'Mobile crisis teams and 988 can be alternatives to 911 for mental health crises.',
    sourceRefs: [],
    tags: ['de-escalation', 'mental-health', 'community'],
  },
  {
    id: 'deescalation-validate-first',
    type: 'multipleChoice',
    deck: legalQuizDecks.deEscalation,
    category: 'de-escalation',
    difficulty: 'easy',
    prompt: 'In de-escalation, what does it mean to "validate" someone?',
    choices: [
      { id: 'a', text: 'Tell them their facts are correct.' },
      { id: 'b', text: 'Acknowledge that their feelings make sense given their experience, even if you disagree with their behavior.' },
      { id: 'c', text: 'Agree with everything they say.' },
      { id: 'd', text: 'Prove to them that their concerns are legitimate using logic.' },
    ],
    correctChoiceId: 'b',
    explanation:
      'Validation means saying "I can see why that would feel scary / frustrating / overwhelming" — acknowledging the emotion as understandable, not necessarily agreeing with the content. People de-escalate faster when they feel understood than when they feel corrected.',
    reinforcement: 'Validate the feeling, not necessarily the facts. "I can see why that feels..."',
    sourceRefs: [],
    tags: ['de-escalation', 'empathy', 'language'],
  },
];

export const getLegalQuizQuestionsByDeck = (deck) =>
  legalQuizQuestions.filter((question) => question.deck === deck);

export const getLegalQuizQuestionsByCategory = (category) =>
  legalQuizQuestions.filter((question) => question.category === category);

export const getLegalQuizQuestionsByTag = (tag) =>
  legalQuizQuestions.filter((question) => question.tags?.includes(tag));

export const getLegalQuizQuestionById = (id) =>
  legalQuizQuestions.find((question) => question.id === id);

export const getLegalQuizRightsTarget = (question) => {
  if (!question) return null;
  if (question.category === '1st-amendment') return '1st';
  if (question.category === '4th-amendment' || question.category === 'warrants') return '4th';
  if (question.category === '5th-amendment') return '5th';
  if (question.category === '6th-amendment') return '6th';
  if (question.category === '14th-amendment') return '14th';
  if (question.tags?.includes('1st-amendment')) return '1st';
  if (question.tags?.includes('4th-amendment')) return '4th';
  if (question.tags?.includes('5th-amendment')) return '5th';
  if (question.tags?.includes('6th-amendment')) return '6th';
  if (question.tags?.includes('14th-amendment')) return '14th';
  return null;
};

const SCENARIO_CATEGORIES = ['door', 'street', 'vehicle', 'workplace', 'border', 'ice-encounter', 'traffic-stop', 'protest', 'first-aid', 'de-escalation'];

export const getLegalQuizScenarioTarget = (question) => {
  if (!question) return null;
  if (SCENARIO_CATEGORIES.includes(question.category)) {
    return question.category;
  }
  return question.tags?.find((tag) => SCENARIO_CATEGORIES.includes(tag)) || null;
};
